import { LitElement, html, nothing } from 'lit';
import { map } from 'lit/directives/map.js';
import { KatapultDialog } from '../katapult-elements/katapult-dialog.js';
import { SharingLink } from '../sharing-link/sharing-link.js';
import { CallCrossServerIdentityAction } from '../../modules/CallCrossServerIdentityAction.js';
import { addFlagListener, removeFlagListener } from '../../modules/FeatureFlags.js';

class ServerAccessSharingLink extends SharingLink {
  async _delete() {
    const { linkId, companyId } = this;
    const targetServerId = this.sharingLink?.recipient?.server_id;

    // If a recipient server was established, remove them from it
    if (targetServerId) {
      await CallCrossServerIdentityAction(globalThis.firebase.auth(), 'REVOKE_COMPANY_SERVER_ACCESS', {
        linkId,
        companyId,
        targetServerId
      });
    }

    await super._delete();
  }

  _deleteWarningTemplate() {
    const { recipient, sender } = this.sharingLink;
    return html`${this._companyDescriptionTemplate(recipient)} will no longer be able to access the ${sender.server} server.`;
  }
}
window.customElements.define('server-access-sharing-link', ServerAccessSharingLink);

class ServerAccessManager extends LitElement {
  static properties = {
    companyId: { type: String },
    serverAccessSharingLinksArray: { type: Array }
  };

  constructor() {
    super();

    this.serverAccessSharingLinksArray = [];
    this.enabledFeatures = {};
  }

  render() {
    return html`
      <paper-header-content>
        <paper-cell slot="header" style="cursor: pointer; gap: var(--sl-spacing-x-small);">
          <material-icon icon="dns"></material-icon>
          <span>Server Access Sharing</span>
        </paper-cell>
        <div slot="content" style="display: flex; flex-direction: column; gap: 12px; padding: 12px;">
          <div style="display: flex; justify-content: center; gap: 8px;">
            ${this.enabledFeatures.can_share_server_access
              ? html`<katapult-button size="18" color="var(--secondary-color)" icon="add_link" callback="generateSharingLink"
                  >Create Sharing Code</katapult-button
                >`
              : nothing}
            <katapult-button size="18" color="var(--secondary-color)" icon="handshake" callback="enterSharingLink"
              >Enter Sharing Code</katapult-button
            >
          </div>
          ${map(
            this.serverAccessSharingLinksArray,
            (item) =>
              html`<server-access-sharing-link
                .linkId=${item.$key}
                .sharingLink=${item}
                .companyId=${this.companyId}
              ></server-access-sharing-link>`
          )}
        </div>
      </paper-header-content>
    `;
  }

  connectedCallback() {
    super.connectedCallback();

    this.canShareServerAccessFlagListener = addFlagListener('can_share_server_access', (enabled) => {
      this.enabledFeatures.can_share_server_access = enabled;
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    removeFlagListener('can_share_server_access', this.canShareServerAccessFlagListener);
  }

  willUpdate(changedProperties) {
    if (changedProperties.has('companyId')) {
      // turn off any existing listeners
      if (this.serverAccessSharingLinksCallback) this.serverAccessSharingLinksRef.off();

      this.serverAccessSharingLinksRef = FirebaseWorker.ref(`photoheight/company_space/${this.companyId}/sharing_links/server_access`);
      // set up a listener for the server access sharing links
      this.serverAccessSharingLinksCallback = this.serverAccessSharingLinksRef.on('value', (snapshot) => {
        this.serverAccessSharingLinksArray = this.computeServerAccessSharingLinksArray(snapshot.val() ?? {});
      });
    }
  }

  computeServerAccessSharingLinksArray(serverAccessSharingLinks) {
    const hideSharingLinksFromCompanies = ['katapult', 'katapult_pro_support'];
    return Object.entries(serverAccessSharingLinks ?? {})
      .map(([key, x]) => {
        // Set the other company's details under the key `otherCompany` for easy access.
        const temp = x.sender?.server_id == window.config.appName ? 'recipient' : 'sender';
        x.otherCompany = x[temp];
        x.$key = key;
        return x;
      })
      .filter((x) => {
        // Only show links that are not pending (or not expired), and neither sent nor received by Katapult.
        const linkIsNotPending = x.pending == null;
        const linkIsNotExpired = x.expires_at == null || x.expires_at > Date.now();
        // We don't want other companies to be able to break automatically established links (which are sent by Katapult).
        const shouldHideSharingLink =
          hideSharingLinksFromCompanies.includes(x.sender?.company_id) == true ||
          hideSharingLinksFromCompanies.includes(x.recipient?.company_id) == true;
        return (linkIsNotPending || linkIsNotExpired) && !shouldHideSharingLink;
      });
  }

  async generateSharingLink(e) {
    const button = e.currentTarget;
    await SharingLink.serverAction({ action: 'create', linkType: 'server_access', companyId: this.companyId });
    button.showSuccess();
  }

  async enterSharingLink() {
    // If a dialog is already open then return.
    if (this.sharingCodeDialog?.opened) return;

    // Create the dialog.
    this.sharingCodeDialog = KatapultDialog.open({
      dialog: {
        title: `Enter Server Access Sharing Code`,
        icon: 'handshake',
        closeOnEscape: true,
        closeOnOutsideClick: true
      },
      template: () => html`
        <div id="inputView">
          <p>Please enter a server access sharing code below:</p>
          <paper-input label="Sharing Code"></paper-input>
        </div>
        <div id="confirmView" style="text-align: center; display: none;">
          <p>
            You are about to establish a server access link with <strong class="companyName"></strong> from the
            <strong class="serverName"></strong> server.
          </p>
          <p>Once complete, you will be able to give your users access to the <strong class="serverName"></strong> server.</p>
          <p>Would you like to continue?</p>
        </div>
        <katapult-button slot="buttons" dialog-dismiss>Cancel</katapult-button>
        <div slot="buttons" style="flex-grow: 1;"></div>
        <katapult-button slot="buttons" id="confirm" justify-icon="right" color="var(--secondary-color)" disabled>Continue</katapult-button>
      `
    });
    // wait for the dialog to open.
    await this.sharingCodeDialog.openStart;
    // Create a placeholder for storing the entered token.
    let token = '';
    // Get the confirm button.
    const button = this.sharingCodeDialog.querySelector('katapult-button[id="confirm"]');
    // Get the input and setup a value listener on it to enable the confirm button when valid.
    const input = this.sharingCodeDialog.querySelector('paper-input');
    // Make sure we have the input and button.
    if (!button || !input) return;

    input.addEventListener('value-changed', (e) => {
      token = e.detail.value?.trim();
      const valid = SharingLink.tokenFormatValid(token);
      input.invalid = token.length && !valid;
      input.errorMessage = input.invalid ? 'Must be a 10 digit code consisting of numbers and letters' : null;
      button.disabled = !valid;
      button.icon = valid ? 'arrow_forward' : null;
    });
    // Click button when enter pressed.
    input.addEventListener('keypress', (e) => e.key == 'Enter' && !button.disabled && button.click());
    // Setup callback on the button to get sharing link.
    button.callback = async () => {
      const { companyId } = this;
      const serverId = window.config.appName;
      const { sharingLink } = await SharingLink.serverAction({ action: 'get', token, companyId }).catch((err) => {
        input.invalid = true;
        input.errorMessage = 'Invalid sharing code';
      });
      if (!sharingLink) {
        input.invalid = true;
        input.errorMessage = 'Invalid sharing code';
      } else if (sharingLink.sender.server_id == serverId) {
        input.invalid = true;
        input.errorMessage = 'Cannot create a server access link between two companies on the same server';
      } else {
        // Update button.
        button.justifyIcon = 'left';
        button.icon = 'check';
        button.innerText = 'Establish Sharing Link';
        button.callback = async () => {
          await SharingLink.serverAction({ action: 'accept', token, companyId });
          setTimeout(() => this.sharingCodeDialog.close(), 2000);
          return true;
        };
        // Populate sharing details.
        Array.from(this.sharingCodeDialog.querySelectorAll('.companyName')).forEach((x) => (x.innerText = sharingLink.sender.company));
        Array.from(this.sharingCodeDialog.querySelectorAll('.serverName')).forEach((x) => (x.innerText = sharingLink.sender.server));
        // Hide input view and show confirmation view.
        this.sharingCodeDialog.querySelector('#inputView').style.display = 'none';
        this.sharingCodeDialog.querySelector('#confirmView').style.display = '';
      }
    };
    // Focus the input.
    setTimeout(() => input.focus());
  }
}

window.customElements.define('server-access-manager', ServerAccessManager);
